import { useState } from "react";

import { Flex, SimpleGrid, Text } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { TextInput } from "components";
import { CreditOptionsResponse, CreditRequestForm, CreditResponse, PreliminaryCredit } from "types";

import { CreditComparisonSource } from "../comparison-source";
import {
  comparisonValuesDiffer,
  formatComparisonDate,
  hasComparisonValue,
  normalizeComparisonValue,
  toComparisonDateValue,
} from "../preliminary-comparison";
import PreliminaryComparisonModal, {
  PreliminaryComparisonRow,
} from "../preliminary-comparison-modal";

import CreditRequestPersonalAddress from "./address";
import CreditRequestPersonalChildren from "./children";
import CreditRequestPersonalInfo from "./info";
import CreditRequestPersonalPartner from "./partner";

interface CreditRequestPersonalProps {
  credit: CreditResponse;
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  visibleFields: string[];
  readOnly?: boolean;
  isBankCredit?: boolean;
  comparisonSource?: CreditComparisonSource | null;
}

type WarningSection = "basic-info" | "address" | "previous-address" | "children" | "partner";
type ComparisonFieldName = Extract<keyof CreditRequestForm, keyof PreliminaryCredit>;
type ComparisonFieldConfig = {
  name: ComparisonFieldName;
  label: string;
  formatValue?: (value: any) => string;
  toFormValue?: (value: any) => any;
  normalizeForComparison?: (value: any) => any;
};

const normalizeBirthYear = (birthYear: number | string | null | undefined) => {
  if (birthYear === undefined || birthYear === null || birthYear === "") {
    return undefined;
  }

  const normalizedBirthYear =
    typeof birthYear === "string" ? Number(birthYear) : birthYear;

  return Number.isNaN(normalizedBirthYear) ? undefined : normalizedBirthYear;
};

const formatChildrenValue = (value: CreditRequestForm["children"]) => {
  if (!value || value.length === 0) {
    return "-";
  }

  return value
    .map((child, index) => {
      const parts = [
        child.birthYear ? `god. rodjenja: ${child.birthYear}` : undefined,
        child.craft === true ? "zanat: da" : child.craft === false ? "zanat: ne" : undefined,
        hasComparisonValue(child.craftAmount) ? `prihod: ${child.craftAmount}` : undefined,
      ].filter(Boolean);

      return `${index + 1}. ${parts.join(", ")}`;
    })
    .join("; ");
};

const normalizeChildrenComparisonValue = (value: CreditRequestForm["children"]) =>
  (value ?? []).map((child) => ({
    birthYear: normalizeBirthYear(child.birthYear),
    craft: child.craft ?? null,
    craftAmount: child.craftAmount ?? null,
  }));

const CreditRequestPersonal = ({
  credit,
  form,
  options,
  visibleFields,
  readOnly = false,
  isBankCredit = false,
  comparisonSource,
}: CreditRequestPersonalProps) => {
  const { t } = useTranslation();
  const currentValues = form.getValues();
  const comparisonCredit = comparisonSource?.credit;
  const comparisonIndicatorType = comparisonSource?.type === "previous" ? "history" : "warning";
  const canApplyComparisonSource = !!comparisonSource;
  const [activeWarningSection, setActiveWarningSection] = useState<WarningSection | null>(null);
  const [childrenSyncVersion, setChildrenSyncVersion] = useState(0);
  const formatYesNoValue = (value: boolean | null) =>
    value === true ? t("yes") : value === false ? t("no") : "-";
  const toChildrenFormValue = (value: any) => {
    if (!Array.isArray(value)) {
      return [];
    }

    const currentChildren = (form.getValues().children ?? []) as NonNullable<
      CreditRequestForm["children"]
    >;

    return value.map((child, index) => ({
      ...currentChildren[index],
      birthYear: normalizeBirthYear(child.birthYear),
      craft: child.craft,
      craftAmount: child.craftAmount,
      creditRequestId: credit.id,
    }));
  };

  const hasAddressData =
    hasComparisonValue(currentValues.postal) ||
    hasComparisonValue(currentValues.city) ||
    hasComparisonValue(currentValues.street) ||
    hasComparisonValue(currentValues.houseNumber) ||
    hasComparisonValue(currentValues.livingFrom);

  const hasPreviousAddressData =
    hasComparisonValue(currentValues.previousPostal) ||
    hasComparisonValue(currentValues.previousCity) ||
    hasComparisonValue(currentValues.previousStreet) ||
    hasComparisonValue(currentValues.previousHouseNumber);

  const hasChildrenData =
    hasComparisonValue(currentValues.maritalStatus) ||
    hasComparisonValue(currentValues.supportsChildren) ||
    (currentValues.children?.some(
      (child) =>
        hasComparisonValue(child.birthYear) ||
        hasComparisonValue(child.craft) ||
        hasComparisonValue(child.craftAmount),
    ) ?? false);

  const hasPartnerData =
    hasComparisonValue(currentValues.partnerOnSameAddress) ||
    hasComparisonValue(currentValues.partnerIncludeInApplication) ||
    hasComparisonValue(currentValues.partnerGender) ||
    hasComparisonValue(currentValues.partnerFirstName) ||
    hasComparisonValue(currentValues.partnerLastName) ||
    hasComparisonValue(currentValues.partnerBirthDate) ||
    hasComparisonValue(currentValues.partnerEmail) ||
    hasComparisonValue(currentValues.partnerCountryCode) ||
    hasComparisonValue(currentValues.partnerPhoneNumber) ||
    hasComparisonValue(currentValues.partnerWorkdayAvailabilityFrom) ||
    hasComparisonValue(currentValues.partnerWorkdayAvailabilityTo) ||
    hasComparisonValue(currentValues.partnerNationality) ||
    hasComparisonValue(currentValues.partnerSaturdayAvailabilityFrom) ||
    hasComparisonValue(currentValues.partnerSaturdayAvailabilityTo) ||
    hasComparisonValue(currentValues.partnerVisaType) ||
    hasComparisonValue(currentValues.partnerVisaFrom);

  const hasBasicInfoData =
    hasComparisonValue(currentValues.gender) ||
    hasComparisonValue(currentValues.birthDate) ||
    hasComparisonValue(currentValues.workdayAvailabilityFrom) ||
    hasComparisonValue(currentValues.workdayAvailabilityTo) ||
    hasComparisonValue(currentValues.nationality) ||
    hasComparisonValue(currentValues.saturdayAvailabilityFrom) ||
    hasComparisonValue(currentValues.saturdayAvailabilityTo) ||
    hasComparisonValue(currentValues.visaType) ||
    hasComparisonValue(currentValues.visaFrom);

  const comparisonHasAddressData =
    hasComparisonValue(comparisonCredit?.postal) ||
    hasComparisonValue(comparisonCredit?.city) ||
    hasComparisonValue(comparisonCredit?.street) ||
    hasComparisonValue(comparisonCredit?.houseNumber) ||
    hasComparisonValue(comparisonCredit?.livingFrom);

  const comparisonHasPreviousAddressData =
    hasComparisonValue(comparisonCredit?.previousPostal) ||
    hasComparisonValue(comparisonCredit?.previousCity) ||
    hasComparisonValue(comparisonCredit?.previousStreet) ||
    hasComparisonValue(comparisonCredit?.previousHouseNumber);

  const comparisonHasChildrenData =
    hasComparisonValue(comparisonCredit?.maritalStatus) ||
    hasComparisonValue(comparisonCredit?.supportsChildren) ||
    (comparisonCredit?.children?.some(
      (child) =>
        hasComparisonValue(child.birthYear) ||
        hasComparisonValue(child.craft) ||
        hasComparisonValue(child.craftAmount),
    ) ?? false);

  const comparisonHasPartnerData =
    hasComparisonValue(comparisonCredit?.partnerOnSameAddress) ||
    hasComparisonValue(comparisonCredit?.partnerIncludeInApplication) ||
    hasComparisonValue(comparisonCredit?.partnerGender) ||
    hasComparisonValue(comparisonCredit?.partnerFirstName) ||
    hasComparisonValue(comparisonCredit?.partnerLastName) ||
    hasComparisonValue(comparisonCredit?.partnerBirthDate) ||
    hasComparisonValue(comparisonCredit?.partnerEmail) ||
    hasComparisonValue(comparisonCredit?.partnerCountryCode) ||
    hasComparisonValue(comparisonCredit?.partnerPhoneNumber) ||
    hasComparisonValue(comparisonCredit?.partnerWorkdayAvailabilityFrom) ||
    hasComparisonValue(comparisonCredit?.partnerWorkdayAvailabilityTo) ||
    hasComparisonValue(comparisonCredit?.partnerNationality) ||
    hasComparisonValue(comparisonCredit?.partnerSaturdayAvailabilityFrom) ||
    hasComparisonValue(comparisonCredit?.partnerSaturdayAvailabilityTo) ||
    hasComparisonValue(comparisonCredit?.partnerVisaType) ||
    hasComparisonValue(comparisonCredit?.partnerVisaFrom);

  const comparisonHasBasicInfoData =
    hasComparisonValue(comparisonCredit?.gender) ||
    hasComparisonValue(comparisonCredit?.birthDate) ||
    hasComparisonValue(comparisonCredit?.workdayAvailabilityFrom) ||
    hasComparisonValue(comparisonCredit?.workdayAvailabilityTo) ||
    hasComparisonValue(comparisonCredit?.nationality) ||
    hasComparisonValue(comparisonCredit?.saturdayAvailabilityFrom) ||
    hasComparisonValue(comparisonCredit?.saturdayAvailabilityTo) ||
    hasComparisonValue(comparisonCredit?.visaType) ||
    hasComparisonValue(comparisonCredit?.visaFrom);

  const basicInfoWarning = !!comparisonCredit &&
    (hasBasicInfoData || comparisonHasBasicInfoData) && (
    comparisonValuesDiffer(comparisonCredit.gender, currentValues.gender) ||
    comparisonValuesDiffer(comparisonCredit.birthDate, currentValues.birthDate) ||
    comparisonValuesDiffer(
      comparisonCredit.workdayAvailabilityFrom,
      currentValues.workdayAvailabilityFrom,
    ) ||
    comparisonValuesDiffer(
      comparisonCredit.workdayAvailabilityTo,
      currentValues.workdayAvailabilityTo,
    ) ||
    comparisonValuesDiffer(comparisonCredit.nationality, currentValues.nationality) ||
    comparisonValuesDiffer(
      comparisonCredit.saturdayAvailabilityFrom,
      currentValues.saturdayAvailabilityFrom,
    ) ||
    comparisonValuesDiffer(
      comparisonCredit.saturdayAvailabilityTo,
      currentValues.saturdayAvailabilityTo,
    ) ||
    comparisonValuesDiffer(comparisonCredit.visaType, currentValues.visaType) ||
    comparisonValuesDiffer(comparisonCredit.visaFrom, currentValues.visaFrom)
  );

  const addressWarning = !!comparisonCredit &&
    (hasAddressData || comparisonHasAddressData) && (
    comparisonValuesDiffer(comparisonCredit.postal, currentValues.postal) ||
    comparisonValuesDiffer(comparisonCredit.city, currentValues.city) ||
    comparisonValuesDiffer(comparisonCredit.street, currentValues.street) ||
    comparisonValuesDiffer(comparisonCredit.houseNumber, currentValues.houseNumber) ||
    comparisonValuesDiffer(comparisonCredit.livingFrom, currentValues.livingFrom)
  );

  const previousAddressWarning = !!comparisonCredit &&
    (hasPreviousAddressData || comparisonHasPreviousAddressData) && (
    comparisonValuesDiffer(comparisonCredit.previousPostal, currentValues.previousPostal) ||
    comparisonValuesDiffer(comparisonCredit.previousCity, currentValues.previousCity) ||
    comparisonValuesDiffer(comparisonCredit.previousStreet, currentValues.previousStreet) ||
    comparisonValuesDiffer(comparisonCredit.previousHouseNumber, currentValues.previousHouseNumber)
  );

  const childrenWarning = !!comparisonCredit &&
    (hasChildrenData || comparisonHasChildrenData) && (
    comparisonValuesDiffer(comparisonCredit.maritalStatus, currentValues.maritalStatus) ||
    comparisonValuesDiffer(comparisonCredit.supportsChildren, currentValues.supportsChildren) ||
    comparisonValuesDiffer(
      normalizeChildrenComparisonValue(comparisonCredit.children),
      normalizeChildrenComparisonValue(currentValues.children),
    )
  );

  const partnerWarning = !!comparisonCredit &&
    (hasPartnerData || comparisonHasPartnerData) && (
    comparisonValuesDiffer(comparisonCredit.partnerOnSameAddress, currentValues.partnerOnSameAddress) ||
    comparisonValuesDiffer(comparisonCredit.partnerIncludeInApplication, currentValues.partnerIncludeInApplication) ||
    comparisonValuesDiffer(comparisonCredit.partnerGender, currentValues.partnerGender) ||
    comparisonValuesDiffer(comparisonCredit.partnerFirstName, currentValues.partnerFirstName) ||
    comparisonValuesDiffer(comparisonCredit.partnerLastName, currentValues.partnerLastName) ||
    comparisonValuesDiffer(comparisonCredit.partnerBirthDate, currentValues.partnerBirthDate) ||
    comparisonValuesDiffer(comparisonCredit.partnerEmail, currentValues.partnerEmail) ||
    comparisonValuesDiffer(comparisonCredit.partnerCountryCode, currentValues.partnerCountryCode) ||
    comparisonValuesDiffer(comparisonCredit.partnerPhoneNumber, currentValues.partnerPhoneNumber) ||
    comparisonValuesDiffer(comparisonCredit.partnerWorkdayAvailabilityFrom, currentValues.partnerWorkdayAvailabilityFrom) ||
    comparisonValuesDiffer(comparisonCredit.partnerWorkdayAvailabilityTo, currentValues.partnerWorkdayAvailabilityTo) ||
    comparisonValuesDiffer(comparisonCredit.partnerNationality, currentValues.partnerNationality) ||
    comparisonValuesDiffer(comparisonCredit.partnerSaturdayAvailabilityFrom, currentValues.partnerSaturdayAvailabilityFrom) ||
    comparisonValuesDiffer(comparisonCredit.partnerSaturdayAvailabilityTo, currentValues.partnerSaturdayAvailabilityTo) ||
    comparisonValuesDiffer(comparisonCredit.partnerVisaType, currentValues.partnerVisaType) ||
    comparisonValuesDiffer(comparisonCredit.partnerVisaFrom, currentValues.partnerVisaFrom)
  );

  const comparisonSections: Record<WarningSection, ComparisonFieldConfig[]> = {
    "basic-info": [
      {
        name: "gender",
        label: t("user.gender.label"),
        formatValue: (value) => (value ? t(`user.gender.options.${value}`) : "-"),
      },
      {
        name: "birthDate",
        label: t("user.birthDate.label"),
        formatValue: (value) => formatComparisonDate(value),
        toFormValue: toComparisonDateValue,
      },
      {
        name: "workdayAvailabilityFrom",
        label: `${t("credit.workdayAvailability.label")} (${t("credit.workdayAvailability.from")})`,
      },
      {
        name: "workdayAvailabilityTo",
        label: `${t("credit.workdayAvailability.label")} (${t("credit.workdayAvailability.to")})`,
      },
      {
        name: "nationality",
        label: t("user.nationality.label"),
        formatValue: (value) => (value ? t(`user.nationality.options.${value}`) : "-"),
      },
      {
        name: "saturdayAvailabilityFrom",
        label: `${t("credit.saturdayAvailability.label")} (${t("credit.saturdayAvailability.from")})`,
      },
      {
        name: "saturdayAvailabilityTo",
        label: `${t("credit.saturdayAvailability.label")} (${t("credit.saturdayAvailability.to")})`,
      },
      {
        name: "visaType",
        label: t("user.visa.label"),
        formatValue: (value) => (value ? t(`user.visa.options.${value}`) : "-"),
      },
      {
        name: "visaFrom",
        label: t("user.visa.from"),
        formatValue: (value) => formatComparisonDate(value),
        toFormValue: toComparisonDateValue,
      },
    ],
    address: [
      { name: "postal", label: t("user.address.postal.label") },
      { name: "city", label: t("user.address.city.label") },
      { name: "street", label: t("user.address.street.label") },
      { name: "houseNumber", label: t("user.address.houseNumber.label") },
      {
        name: "livingFrom",
        label: t("user.livingFrom.label"),
        formatValue: (value) => formatComparisonDate(value, "month"),
        toFormValue: toComparisonDateValue,
      },
    ],
    "previous-address": [
      { name: "previousPostal", label: t("user.address.postal.label") },
      { name: "previousCity", label: t("user.address.city.label") },
      { name: "previousStreet", label: t("user.address.street.label") },
      { name: "previousHouseNumber", label: t("user.address.houseNumber.label") },
    ],
    children: [
      {
        name: "maritalStatus",
        label: t("user.maritalStatus.label"),
        formatValue: (value) =>
          value ? t(`user.maritalStatus.options.${value}`) : "-",
      },
      {
        name: "supportsChildren",
        label: t("credit.supportsChildren.label"),
        formatValue: formatYesNoValue,
      },
      {
        name: "children",
        label: t("credit.children.label"),
        formatValue: formatChildrenValue,
        toFormValue: toChildrenFormValue,
        normalizeForComparison: normalizeChildrenComparisonValue,
      },
    ],
    partner: [
      {
        name: "partnerOnSameAddress",
        label: t("credit.partnerOnSameAddress.label"),
        formatValue: formatYesNoValue,
      },
      {
        name: "partnerIncludeInApplication",
        label: t("credit.partnerIncludeInApplication.label"),
        formatValue: formatYesNoValue,
      },
      {
        name: "partnerGender",
        label: t("credit.partnerGender.label"),
        formatValue: (value) => (value ? t(`user.gender.options.${value}`) : "-"),
      },
      { name: "partnerFirstName", label: t("credit.partnerFirstName.label") },
      { name: "partnerLastName", label: t("credit.partnerLastName.label") },
      {
        name: "partnerBirthDate",
        label: t("credit.partnerBirthDate.label"),
        formatValue: (value) => formatComparisonDate(value),
        toFormValue: toComparisonDateValue,
      },
      { name: "partnerEmail", label: t("credit.partnerEmail.label") },
      { name: "partnerCountryCode", label: t("credit.partner.phone") },
      { name: "partnerPhoneNumber", label: t("credit.partnerPhoneNumber.label") },
      {
        name: "partnerWorkdayAvailabilityFrom",
        label: `${t("credit.workdayAvailability.label")} (${t("credit.workdayAvailability.from")})`,
      },
      {
        name: "partnerWorkdayAvailabilityTo",
        label: `${t("credit.workdayAvailability.label")} (${t("credit.workdayAvailability.to")})`,
      },
      {
        name: "partnerNationality",
        label: t("credit.partnerNationality.label"),
        formatValue: (value) => (value ? t(`user.nationality.options.${value}`) : "-"),
      },
      {
        name: "partnerSaturdayAvailabilityFrom",
        label: `${t("credit.saturdayAvailability.label")} (${t("credit.saturdayAvailability.from")})`,
      },
      {
        name: "partnerSaturdayAvailabilityTo",
        label: `${t("credit.saturdayAvailability.label")} (${t("credit.saturdayAvailability.to")})`,
      },
      {
        name: "partnerVisaType",
        label: t("credit.partnerVisaType.label"),
        formatValue: (value) => (value ? t(`user.visa.options.${value}`) : "-"),
      },
      {
        name: "partnerVisaFrom",
        label: t("credit.partnerVisaType.from"),
        formatValue: (value) => formatComparisonDate(value),
        toFormValue: toComparisonDateValue,
      },
    ],
  };

  const fieldValuesDiffer = (field: ComparisonFieldConfig) => {
    if (!comparisonCredit) {
      return false;
    }

    const comparisonValue = field.normalizeForComparison
      ? field.normalizeForComparison(comparisonCredit[field.name])
      : comparisonCredit[field.name];
    const currentValue = field.normalizeForComparison
      ? field.normalizeForComparison(currentValues[field.name])
      : currentValues[field.name];

    return comparisonValuesDiffer(comparisonValue, currentValue);
  };

  const activeSectionFields = activeWarningSection ? comparisonSections[activeWarningSection] : [];
  const comparisonRows: PreliminaryComparisonRow[] =
    activeWarningSection && comparisonCredit
      ? activeSectionFields
          .filter(fieldValuesDiffer)
          .map((field) => {
            const formatValue =
              field.formatValue ?? ((value: any) => String(normalizeComparisonValue(value) ?? "-"));

            return {
              key: String(field.name),
              label: field.label,
              source: <Text>{formatValue(comparisonCredit[field.name])}</Text>,
              current: <Text>{formatValue(currentValues[field.name])}</Text>,
            };
          })
      : [];

  const applyComparisonValues = () => {
    if (!activeWarningSection || !comparisonCredit) {
      return;
    }

    comparisonSections[activeWarningSection]
      .filter(fieldValuesDiffer)
      .forEach((field) => {
        const nextValue = field.toFormValue
          ? field.toFormValue(comparisonCredit[field.name])
          : comparisonCredit[field.name] ?? undefined;

        form.setFieldValue(field.name, nextValue as any);
      });

    if (activeWarningSection === "children") {
      setChildrenSyncVersion((value) => value + 1);
    }

    setActiveWarningSection(null);
  };

  return (
    <Flex direction="column" p="md" gap="lg">
      <CreditRequestPersonalInfo
        form={form}
        options={options}
        visibleFields={visibleFields}
        readOnly={readOnly}
        hasWarning={basicInfoWarning}
        indicatorType={comparisonIndicatorType}
        onWarningClick={() => setActiveWarningSection("basic-info")}
      />
      <CreditRequestPersonalAddress
        form={form}
        visibleFields={visibleFields}
        readOnly={readOnly}
        isBankCredit={isBankCredit}
        hasWarning={addressWarning}
        hasPreviousWarning={previousAddressWarning}
        indicatorType={comparisonIndicatorType}
        onWarningClick={() => setActiveWarningSection("address")}
        onPreviousWarningClick={() => setActiveWarningSection("previous-address")}
      />
      <CreditRequestPersonalChildren
        credit={credit}
        form={form}
        options={options}
        readOnly={readOnly}
        hasWarning={childrenWarning}
        indicatorType={comparisonIndicatorType}
        onWarningClick={() => setActiveWarningSection("children")}
        syncChildrenValues={currentValues.children}
        syncSupportsChildren={currentValues.supportsChildren}
        syncVersion={childrenSyncVersion}
      />
      {visibleFields.includes("partnerOnSameAddress") && (
        <CreditRequestPersonalPartner
          form={form}
          options={options}
          visibleFields={visibleFields}
          readOnly={readOnly}
          isBankCredit={isBankCredit}
          hasWarning={partnerWarning}
          indicatorType={comparisonIndicatorType}
          onWarningClick={() => setActiveWarningSection("partner")}
        />
      )}

      <SimpleGrid cols={4} mt="lg">
        <TextInput
          label={t("credit.comment.singular")}
          key={form.key("comment2")}
          {...form.getInputProps("comment2")}
          disabled={readOnly}
        />
      </SimpleGrid>

      <PreliminaryComparisonModal
        opened={activeWarningSection !== null}
        onClose={() => setActiveWarningSection(null)}
        onApply={applyComparisonValues}
        title={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.title"
            : "credit.preliminaryComparison.title",
        )}
        emptyText={t("credit.preliminaryComparison.empty")}
        fieldLabel={t("credit.preliminaryComparison.field")}
        sourceLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.previous"
            : "credit.preliminaryComparison.preliminary",
        )}
        currentLabel={t("credit.preliminaryComparison.active")}
        applyLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.apply"
            : "credit.preliminaryComparison.apply",
        )}
        cancelLabel={t("credit.preliminaryComparison.cancel")}
        rows={comparisonRows}
        showApplyButton={canApplyComparisonSource}
      />
    </Flex>
  );
};

export default CreditRequestPersonal;
